import usePrevious from '@hooks/use-previous'
import { PRODUCT_CARD_DIMENSIONS } from '@modules/showroom/productCollection/constants/uiConstants'
import { PaginatedHierarchyItemsDataStore } from '@stores/paginated-hierarchy-items/PaginatedHierarchyItemsDataStore'
import {
  ItemType,
  PaginatedHierarchyItemsUIStore
} from '@stores/paginated-hierarchy-items/PaginatedHierarchyItemsUIStore'
import { useObserver } from 'mobx-react-lite'
import React, { Context, useContext, useMemo } from 'react'
import { ProductCardSizes } from '@constants'
import { View } from '../constants/views'
import { KPIDataStore } from '../stores/view/KPI/KPIDataStore'
import { KPIUIStore } from '../stores/view/KPI/KPIUIStore'
import { ListDataStore } from '../stores/view/List/ListDataStore'
import { ListUIStore } from '../stores/view/List/ListUIStore'
import { ProductDataStore } from '../stores/view/Product/ProductDataStore'
import { ProductUIStore } from '../stores/view/Product/ProductUIStore'
import { AddProductToOrderUIStore } from '../stores/view/AddProductToOrder/AddProductToOrderUIStore'
import { AddProductToOrderDataStore } from '../stores/view/AddProductToOrder/AddProductToOrderDataStore'
import OrderContext from './order-context'
import {
  STATIC_FILTERS,
  HIERARCHY_ELEMENT_HEIGHT
} from '@modules/order/constants/uiConstants'
import { stores } from '@stores'
import { EnhancedListDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListDataStore'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import { InitialAllocationUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationUIStore'
import { InitialAllocationDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationDataStore'

const ViewContext: Context<{
  uiStore:
    | PaginatedHierarchyItemsUIStore<PaginatedHierarchyItemsDataStore>
    | EnhancedListUIStore<EnhancedListDataStore>
    | ListUIStore
    | ProductUIStore
    | AddProductToOrderUIStore
    | InitialAllocationUIStore<InitialAllocationDataStore>
  previousState: any
}> = React.createContext({
  uiStore: null,
  previousState: null
})

const TableViewDimensions = {
  [ItemType.ROOT]: {
    height: 28
  },
  [ItemType.CATEGORY]: {
    height: HIERARCHY_ELEMENT_HEIGHT
  },
  [ItemType.INDIVIDUAL]: {
    height: 235,
    width: Infinity
  },
  [ItemType.PAGE]: {
    height: 234
  }
}

const CardViewDimensions = {
  [ItemType.ROOT]: {
    height: 0
  },
  [ItemType.CATEGORY]: {
    height: HIERARCHY_ELEMENT_HEIGHT
  },
  [ItemType.INDIVIDUAL]: PRODUCT_CARD_DIMENSIONS[ProductCardSizes.Medium],
  [ItemType.PAGE]: {
    height: PRODUCT_CARD_DIMENSIONS[ProductCardSizes.Medium].height
  }
}

const ViewStoreProvider = {
  [View.Product]: () =>
    new ProductUIStore(new ProductDataStore(STATIC_FILTERS), CardViewDimensions),
  [View.List]: () => {
    const {
      productConfiguration: { enhancedViewEnabled, initialAllocationActivated }
    } = stores
    if (enhancedViewEnabled) {
      if (initialAllocationActivated) {
        return new InitialAllocationUIStore(
          new InitialAllocationDataStore(STATIC_FILTERS),
          TableViewDimensions
        )
      }
      return new EnhancedListUIStore(
        new EnhancedListDataStore(STATIC_FILTERS),
        TableViewDimensions
      )
    }
    return new ListUIStore(new ListDataStore(STATIC_FILTERS), TableViewDimensions)
  },
  [View.KPI]: () => new KPIUIStore(new KPIDataStore(STATIC_FILTERS), TableViewDimensions),
  [View.AddProduct]: () =>
    new AddProductToOrderUIStore(
      new AddProductToOrderDataStore(STATIC_FILTERS),
      CardViewDimensions
    )
}

export const ViewContextProvider = ({ children }) =>
  useObserver(function useHook() {
    const {
      productConfiguration: { initialAllocationActivated }
    } = stores
    const {
      uiStore: { view }
    } = useContext(OrderContext)
    const uiStore = useMemo(() => {
      if (initialAllocationActivated) {
        return ViewStoreProvider[view]() // fooling lint
      }
      return ViewStoreProvider[view]()
    }, [view, initialAllocationActivated])
    const previousState = usePrevious(uiStore)
    const viewContextValue = useMemo(
      () => ({
        uiStore,
        previousState
      }),
      [uiStore, previousState]
    )
    return (
      <ViewContext.Provider value={viewContextValue}>{children}</ViewContext.Provider>
    )
  })

export default ViewContext
